import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  Collapse,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Typography,
  Spin,
  Upload,
  message,
  Divider,
  Alert,
  Empty,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SaveOutlined,
  SendOutlined,
  UploadOutlined,
  CloseCircleOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { api } from "@/services/api";
import { API_ENDPOINTS } from "@/constants";
import { resolveMediaUrl } from "@/utils/url";
import { CardFieldEditor } from "@/components/CardFieldEditor";
import type { ApiResponse, Board, Deck, Subject, Chapter } from "@/types";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";

const { Title, Text } = Typography;
const { TextArea } = Input;

interface CardFormData {
  localId: string;
  id?: string;
  title: string;
  frontText: string;
  backText: string;
  imageUrl: string;
  hint: string;
  explanation: string;
  example: string;
  commonMistake: string;
  selfCheck: string;
  difficulty: 'EASY' | 'MEDIUM' | 'HARD' | null;
}

interface DeckFormValues {
  topicName: string;
  description: string;
  subjectId: string | undefined;
  chapterId: string | undefined;
  deadline: Dayjs | null;
}

const MIN_CARDS = 4;

function generateLocalId(): string {
  return `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

export default function DeckEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<DeckFormValues>();

  const [cards, setCards] = useState<CardFormData[]>([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState<
    string | undefined
  >(undefined);
  const [selectedBoardId, setSelectedBoardId] = useState<string | undefined>(
    undefined,
  );

  const [showFrontErrors, setShowFrontErrors] = useState(false);
  const addCardButtonRef = useRef<HTMLButtonElement>(null);
  const hasEmptyFront =
    cards.length > 0 && cards.some((c) => !c.frontText.trim());
  const hasEmptyTitle = cards.length > 0 && cards.some((c) => !c.title.trim());

  // Fetch existing deck if editing
  const {
    data: deckResponse,
    isLoading: loadingDeck,
    error: deckError,
  } = useQuery({
    queryKey: ["teacher", "deck", id],
    queryFn: () =>
      api.get<never, ApiResponse<Deck>>(`${API_ENDPOINTS.DECKS}/${id}`),
    enabled: isEditing,
  });

  // Redirect if deck fetch fails
  useEffect(() => {
    if (deckError && isEditing) {
      message.error("Failed to load deck. Redirecting...");
      navigate("/teacher/decks");
    }
  }, [deckError, isEditing, navigate]);

  // Fetch max cards config
  const { data: limitsResponse } = useQuery({
    queryKey: ["config", "limits"],
    queryFn: () =>
      api.get<never, ApiResponse<{ maxCardsPerDeck: number }>>("/admin/config/limits"),
    staleTime: 5 * 60 * 1000,
  });
  const maxCards: number = limitsResponse?.data?.maxCardsPerDeck ?? 10;

  // Fetch teacher's own profile to get their allowed subjectIds
  const { data: meResponse } = useQuery({
    queryKey: ["users", "me"],
    queryFn: () => api.get<never, ApiResponse<{ subjectIds: string[] }>>(API_ENDPOINTS.USERS_ME),
    staleTime: 5 * 60 * 1000,
  });
  const teacherSubjectIds: string[] = meResponse?.data?.subjectIds ?? [];

  // Fetch boards (includes subjects)
  const { data: boardsResponse } = useQuery({
    queryKey: ["curriculum", "boards"],
    queryFn: () =>
      api.get<never, ApiResponse<(Board & { subjects: Subject[] })[]>>(
        API_ENDPOINTS.CURRICULUM_BOARDS,
      ),
    staleTime: 5 * 60 * 1000,
  });

  // Fetch chapters filtered by subject
  const { data: chaptersResponse } = useQuery({
    queryKey: ["curriculum", "chapters", selectedSubjectId],
    queryFn: () =>
      api.get<never, ApiResponse<Chapter[]>>(
        `${API_ENDPOINTS.CURRICULUM_SUBJECTS}/${selectedSubjectId}/chapters`,
      ),
    enabled: Boolean(selectedSubjectId),
  });

  const allBoards = boardsResponse?.data ?? [];
  // Filter boards/subjects to only what the teacher teaches (if profile is set)
  const boards = useMemo(() =>
    teacherSubjectIds.length > 0
      ? allBoards
          .map((b) => ({ ...b, subjects: b.subjects.filter((s) => teacherSubjectIds.includes(s.id)) }))
          .filter((b) => b.subjects.length > 0)
      : allBoards,
    [allBoards, teacherSubjectIds],
  );
  const subjects = boards.find((b) => b.id === selectedBoardId)?.subjects ?? [];
  const chapters = chaptersResponse?.data ?? [];

  // Auto-select board when teacher only teaches one board and no board is selected yet
  useEffect(() => {
    if (!isEditing && boards.length === 1 && !selectedBoardId) {
      setSelectedBoardId(boards[0].id);
    }
  }, [boards, isEditing, selectedBoardId]);

  // Populate form when deck data loads
  useEffect(() => {
    if (deckResponse?.data) {
      const deck = deckResponse.data;
      form.setFieldsValue({
        topicName: deck.topicName,
        description: deck.description ?? "",
        subjectId: deck.subjectId ?? undefined,
        chapterId: deck.chapterId ?? undefined,
        deadline: deck.deadline ? dayjs(deck.deadline) : null,
      });
      setSelectedSubjectId(deck.subjectId ?? undefined);

      // Restore board selection if we can derive it from the subject
      if (deck.subjectId && boards.length > 0) {
        const board = boards.find((b) =>
          b.subjects.some((s) => s.id === deck.subjectId),
        );
        if (board) setSelectedBoardId(board.id);
      }

      if (deck.cards && deck.cards.length > 0) {
        const sorted = [...deck.cards].sort(
          (a, b) => a.sequenceOrder - b.sequenceOrder,
        );
        setCards(
          sorted.map((c) => ({
            localId: generateLocalId(),
            id: c.id,
            title: c.title ?? "",
            frontText: c.frontText ?? "",
            backText: c.backText ?? "",
            imageUrl: c.imageUrl ?? "",
            hint: c.hint ?? "",
            explanation: c.explanation ?? "",
            example: c.example ?? "",
            commonMistake: c.commonMistake ?? "",
            selfCheck: c.selfCheck ?? "",
            difficulty: c.difficulty ?? null,
          })),
        );
      }
    }
  }, [deckResponse, form, boards]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: (payload: {
      topicName: string;
      description?: string;
      subjectId?: string;
      chapterId?: string;
      deadline?: string;
      cards: Array<{
        id?: string;
        title?: string;
        frontText: string;
        backText?: string;
        imageUrl?: string;
        sequenceOrder: number;
        hint?: string;
        explanation?: string;
        example?: string;
        commonMistake?: string;
        selfCheck?: string;
        difficulty?: 'EASY' | 'MEDIUM' | 'HARD' | null;
      }>;
    }) => {
      if (isEditing) {
        return api.patch<never, ApiResponse<Deck>>(
          `${API_ENDPOINTS.DECKS}/${id}`,
          payload,
        );
      }
      return api.post<never, ApiResponse<Deck>>(API_ENDPOINTS.DECKS, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher", "decks"] });
      if (isEditing) {
        queryClient.invalidateQueries({ queryKey: ["teacher", "deck", id] });
      }
      message.success(isEditing ? "Deck updated" : "Deck created");
      navigate("/teacher/decks");
    },
    onError: (err: { error?: { message?: string } }) => {
      message.error(err?.error?.message ?? "Failed to save deck");
    },
  });

  const publishMutation = useMutation({
    mutationFn: (deckId: string) =>
      api.post<never, ApiResponse<Deck>>(
        `${API_ENDPOINTS.DECKS}/${deckId}/publish`,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teacher", "decks"] });
      message.success("Deck published");
      navigate("/teacher/decks");
    },
    onError: () => {
      message.error("Failed to publish deck");
    },
  });

  const buildPayload = useCallback(() => {
    const values = form.getFieldsValue();
    return {
      topicName: values.topicName,
      description: values.description || undefined,
      subjectId: values.subjectId || undefined,
      chapterId: values.chapterId || undefined,
      deadline: values.deadline?.toISOString(),
      cards: cards.map((c, index) => ({
        id: c.id,
        title: c.title,
        frontText: c.frontText,
        backText: c.backText || undefined,
        imageUrl: c.imageUrl || undefined,
        sequenceOrder: index + 1,
        hint: c.hint || undefined,
        explanation: c.explanation || undefined,
        example: c.example || undefined,
        commonMistake: c.commonMistake || undefined,
        selfCheck: c.selfCheck || undefined,
        difficulty: c.difficulty || undefined,
      })),
    };
  }, [cards, form]);

  const handleSave = async () => {
    setShowFrontErrors(true);
    if (hasEmptyFront || hasEmptyTitle) return;
    try {
      await form.validateFields();
      saveMutation.mutate(buildPayload());
    } catch {
      message.warning("Please fill in all required fields");
    }
  };

  const handlePublish = async () => {
    setShowFrontErrors(true);
    if (hasEmptyFront || hasEmptyTitle) return;

    // Step 1: client-side validation only
    try {
      await form.validateFields();
    } catch {
      message.warning("Please fill in all required fields");
      return;
    }

    if (cards.length < MIN_CARDS) {
      message.warning(`At least ${MIN_CARDS} cards are required to publish`);
      return;
    }

    // Step 2: API calls — show server errors directly
    try {
      const payload = buildPayload();
      if (isEditing) {
        await api.patch(`${API_ENDPOINTS.DECKS}/${id}`, payload);
        publishMutation.mutate(id as string);
      } else {
        const res = await api.post<never, ApiResponse<Deck>>(
          API_ENDPOINTS.DECKS,
          payload,
        );
        if (res.data?.id) {
          publishMutation.mutate(res.data.id);
        }
      }
    } catch (err: unknown) {
      const apiErr = err as { error?: { message?: string } };
      message.error(apiErr?.error?.message ?? "Failed to save deck");
    }
  };

  // Card management
  const addCard = () => {
    if (cards.length >= maxCards) {
      message.warning(`Maximum ${maxCards} cards allowed per deck`);
      return;
    }
    setCards((prev) => [
      ...prev,
      {
        localId: generateLocalId(),
        title: "",
        frontText: "",
        backText: "",
        imageUrl: "",
        hint: "",
        explanation: "",
        example: "",
        commonMistake: "",
        selfCheck: "",
        difficulty: null,
      },
    ]);
    setTimeout(() => addCardButtonRef.current?.focus(), 0);
  };

  const updateCard = (
    localId: string,
    field: keyof CardFormData,
    value: string | null,
  ) => {
    setCards((prev) =>
      prev.map((c) => (c.localId === localId ? { ...c, [field]: value } : c)),
    );
  };

  const removeCard = (localId: string) => {
    setCards((prev) => prev.filter((c) => c.localId !== localId));
  };

  const moveCard = (index: number, direction: "up" | "down") => {
    setCards((prev) => {
      const copy = [...prev];
      const targetIndex = direction === "up" ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= copy.length) return prev;
      [copy[index], copy[targetIndex]] = [copy[targetIndex], copy[index]];
      return copy;
    });
  };

  const handleImageUpload = async (localId: string, file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post<never, ApiResponse<{ url: string }>>(
        API_ENDPOINTS.UPLOAD_IMAGE,
        formData,
        {
          headers: { "Content-Type": undefined },
          transformRequest: [(data: FormData) => data],
        },
      );
      if (res.data?.url) {
        updateCard(localId, "imageUrl", res.data.url);
        message.success("Image uploaded");
      }
    } catch {
      message.error("Image upload failed");
    }
  };

  if (isEditing && loadingDeck) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", paddingTop: 120 }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }}>
      <div style={{ marginBottom: 20 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/teacher/decks")}
          style={{ marginBottom: 8 }}
        >
          Back to Decks
        </Button>
        <Title level={3} style={{ margin: 0 }}>
          {isEditing ? "Edit Deck" : "Create New Deck"}
        </Title>
      </div>

      <Card style={{ borderRadius: 12, marginBottom: 20 }}>
        <Title level={5}>Deck Details</Title>
        <Form form={form} layout="vertical" requiredMark="optional">
          <Form.Item
            name="topicName"
            label="Topic Name"
            rules={[
              { required: true, message: "Topic name is required" },
              { max: 100, message: "Max 100 characters" },
            ]}
          >
            <Input
              placeholder="e.g., Photosynthesis Basics"
              maxLength={100}
              showCount
            />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ max: 500, message: "Max 500 characters" }]}
          >
            <TextArea
              placeholder="Brief description of this deck..."
              maxLength={500}
              showCount
              rows={3}
            />
          </Form.Item>

          <Space size="middle" style={{ display: "flex" }}>
            <Form.Item label="Board" style={{ flex: 1, minWidth: 160 }}>
              <Select
                placeholder="All boards"
                allowClear
                showSearch
                optionFilterProp="label"
                value={selectedBoardId}
                onChange={(value: string | undefined) => {
                  setSelectedBoardId(value);
                  setSelectedSubjectId(undefined);
                  form.setFieldValue("subjectId", undefined);
                  form.setFieldValue("chapterId", undefined);
                }}
                options={boards.map((b) => ({
                  value: b.id,
                  label: b.name,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="subjectId"
              label="Subject"
              style={{ flex: 1, minWidth: 160 }}
            >
              <Select
                placeholder={
                  selectedBoardId ? "Select subject" : "Select a board first"
                }
                allowClear
                showSearch
                optionFilterProp="label"
                disabled={!selectedBoardId}
                onChange={(value: string | undefined) => {
                  setSelectedSubjectId(value);
                  form.setFieldValue("chapterId", undefined);
                }}
                options={subjects.map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="chapterId"
              label="Chapter"
              style={{ flex: 1, minWidth: 160 }}
            >
              <Select
                placeholder={
                  selectedSubjectId
                    ? "Select chapter"
                    : "Select a subject first"
                }
                allowClear
                showSearch
                optionFilterProp="label"
                disabled={!selectedSubjectId}
                options={chapters.map((c) => ({
                  value: c.id,
                  label: c.name,
                }))}
              />
            </Form.Item>
          </Space>

          <Form.Item name="deadline" label="Deadline (optional)">
            <DatePicker
              style={{ width: "100%" }}
              disabledDate={(current) =>
                current && current.valueOf() < Date.now()
              }
            />
          </Form.Item>
        </Form>
      </Card>

      <Card style={{ borderRadius: 12, marginBottom: 20 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <div>
            <Title level={5} style={{ margin: 0 }}>
              Cards
            </Title>
            <Text type="secondary">
              {cards.length} / {maxCards} cards
              {cards.length < MIN_CARDS && ` (min ${MIN_CARDS} to publish)`}
            </Text>
          </div>
          <Button
            ref={addCardButtonRef}
            icon={<PlusOutlined />}
            onClick={addCard}
            disabled={cards.length >= maxCards}
          >
            Add Card
          </Button>
        </div>

        {cards.length < MIN_CARDS && (
          <Alert
            message={`You need at least ${MIN_CARDS} cards to publish this deck.`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {cards.length >= maxCards && (
          <Alert
            message={`Maximum ${maxCards} cards reached. Remove a card to add more.`}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {[...cards].reverse().map((card, displayIndex) => {
          const index = cards.findIndex((c) => c.localId === card.localId);
          return (
            <div key={card.localId}>
              {displayIndex > 0 && <Divider style={{ margin: "12px 0" }} />}
              <div
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "flex-start",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    paddingTop: 4,
                  }}
                >
                  <Text
                    strong
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: "#f1f5f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 13,
                    }}
                  >
                    {index + 1}
                  </Text>
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowUpOutlined />}
                    disabled={index === 0}
                    onClick={() => moveCard(index, "up")}
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<ArrowDownOutlined />}
                    disabled={index === cards.length - 1}
                    onClick={() => moveCard(index, "down")}
                  />
                </div>

                <div style={{ flex: 1 }}>
                  <Input
                    placeholder="Card title (required, max 100 chars)"
                    value={card.title}
                    onChange={(e) =>
                      updateCard(card.localId, "title", e.target.value)
                    }
                    maxLength={100}
                    status={
                      showFrontErrors && !card.title.trim() ? "error" : ""
                    }
                    style={{
                      marginBottom:
                        showFrontErrors && !card.title.trim() ? 4 : 8,
                    }}
                  />
                  {showFrontErrors && !card.title.trim() && (
                    <Text
                      type="danger"
                      style={{
                        fontSize: 12,
                        marginBottom: 8,
                        display: "block",
                      }}
                    >
                      Card {index + 1}: Title is required
                    </Text>
                  )}
                  <div style={{ marginBottom: 8 }}>
                    <CardFieldEditor
                      label="Front"
                      value={card.frontText}
                      onChange={(v) => updateCard(card.localId, "frontText", v)}
                      placeholder="Front content (supports Markdown + LaTeX)"
                      maxLength={500}
                      rows={3}
                      error={showFrontErrors && !card.frontText.trim()}
                    />
                    {showFrontErrors && !card.frontText.trim() && (
                      <Text
                        type="danger"
                        style={{ fontSize: 12, marginTop: 4, display: "block" }}
                      >
                        Card {index + 1}: Front content is required
                      </Text>
                    )}
                  </div>
                  <CardFieldEditor
                    label="Back"
                    value={card.backText}
                    onChange={(v) => updateCard(card.localId, "backText", v)}
                    placeholder="Back content — leave empty for concept card"
                    maxLength={500}
                    rows={3}
                    optional
                  />
                  <div
                    style={{
                      marginTop: 8,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <Upload
                      accept="image/*"
                      showUploadList={false}
                      maxCount={1}
                      beforeUpload={(file) => {
                        handleImageUpload(card.localId, file);
                        return false;
                      }}
                    >
                      <Button size="small" icon={<UploadOutlined />}>
                        {card.imageUrl ? "Change Image" : "Add Image"}
                      </Button>
                    </Upload>
                    {card.imageUrl && (
                      <div
                        style={{
                          position: "relative",
                          display: "inline-block",
                        }}
                      >
                        <img
                          src={resolveMediaUrl(card.imageUrl)}
                          alt="Card"
                          style={{
                            width: 48,
                            height: 48,
                            objectFit: "cover",
                            borderRadius: 6,
                            border: "1px solid #e2e8f0",
                          }}
                        />
                        <CloseCircleOutlined
                          onClick={() =>
                            updateCard(card.localId, "imageUrl", "")
                          }
                          style={{
                            position: "absolute",
                            top: -6,
                            right: -6,
                            fontSize: 16,
                            color: "#ff4d4f",
                            cursor: "pointer",
                            background: "#fff",
                            borderRadius: "50%",
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <Collapse
                    ghost
                    style={{ marginTop: 8 }}
                    items={[
                      {
                        key: "advanced",
                        label: "Advanced (hint, explanation, difficulty...)",
                        children: (
                          <>
                            <Form.Item label="Difficulty" style={{ marginBottom: 12 }}>
                              <Select
                                allowClear
                                placeholder="Select difficulty"
                                value={card.difficulty || undefined}
                                onChange={(val: string | undefined) =>
                                  updateCard(card.localId, "difficulty", val || null)
                                }
                                options={[
                                  { value: "EASY", label: "Easy" },
                                  { value: "MEDIUM", label: "Medium" },
                                  { value: "HARD", label: "Hard" },
                                ]}
                              />
                            </Form.Item>
                            <Form.Item label="Hint (shown before answer)" style={{ marginBottom: 12 }}>
                              <TextArea
                                maxLength={500}
                                showCount
                                rows={2}
                                value={card.hint}
                                onChange={(e) =>
                                  updateCard(card.localId, "hint", e.target.value)
                                }
                              />
                            </Form.Item>
                            <Form.Item label="Explanation (Why?)" style={{ marginBottom: 12 }}>
                              <TextArea
                                maxLength={500}
                                showCount
                                rows={2}
                                value={card.explanation}
                                onChange={(e) =>
                                  updateCard(card.localId, "explanation", e.target.value)
                                }
                              />
                            </Form.Item>
                            <Form.Item label="Example" style={{ marginBottom: 12 }}>
                              <TextArea
                                maxLength={500}
                                showCount
                                rows={2}
                                value={card.example}
                                onChange={(e) =>
                                  updateCard(card.localId, "example", e.target.value)
                                }
                              />
                            </Form.Item>
                            <Form.Item label="Common Mistake" style={{ marginBottom: 12 }}>
                              <TextArea
                                maxLength={500}
                                showCount
                                rows={2}
                                value={card.commonMistake}
                                onChange={(e) =>
                                  updateCard(card.localId, "commonMistake", e.target.value)
                                }
                              />
                            </Form.Item>
                            <Form.Item label="Self-Check" style={{ marginBottom: 0 }}>
                              <TextArea
                                maxLength={500}
                                showCount
                                rows={2}
                                value={card.selfCheck}
                                onChange={(e) =>
                                  updateCard(card.localId, "selfCheck", e.target.value)
                                }
                              />
                            </Form.Item>
                          </>
                        ),
                      },
                    ]}
                  />
                </div>

                <Popconfirm
                  title="Delete card"
                  description="Are you sure you want to delete this card?"
                  onConfirm={() => removeCard(card.localId)}
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancel"
                >
                  <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </div>
            </div>
          );
        })}

        {cards.length === 0 && (
          <Empty
            description="No cards yet. Add your first card!"
            style={{ padding: 24 }}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={addCard}>
              Add Card
            </Button>
          </Empty>
        )}
      </Card>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 12,
          marginBottom: 40,
        }}
      >
        <Button onClick={() => navigate("/teacher/decks")}>Cancel</Button>
        <Button
          icon={<SaveOutlined />}
          loading={saveMutation.isPending}
          onClick={handleSave}
        >
          Save as Draft
        </Button>
        <Button
          type="primary"
          icon={<SendOutlined />}
          disabled={cards.length < MIN_CARDS || hasEmptyFront || hasEmptyTitle}
          loading={publishMutation.isPending}
          onClick={handlePublish}
        >
          Publish
        </Button>
      </div>
    </div>
  );
}
